import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { KalkiLogo } from "@/components/KalkiLogo";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEMO = [
  { label: "Admin", email: "admin@kalkiai.trade", password: "Admin@Kalki2026" },
  { label: "Channel Partner", email: "partner@kalkiai.trade", password: "Partner@Kalki2026" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "ADMIN" ? "/dashboard" : "/partner", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative z-[1]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] border-r border-[#262626] bg-[#0D0D0D] px-14 py-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12),transparent_70%)]" />
        <KalkiLogo size={40} />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] font-semibold mb-4">Institutional Copy-Trading</div>
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight text-[#F5F5F5]">
            Command your<br />master &amp; copy<br />accounts with<br /><span className="text-[#D4AF37]">precision.</span>
          </h1>
          <p className="text-[#A3A3A3] mt-6 max-w-md text-[15px] leading-relaxed">
            A premium terminal to connect exchanges, mirror strategies across accounts, and track portfolio performance in real time.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-[#737373]">
          <ShieldCheck className="w-4 h-4 text-[#10B981]" />
          API secrets never touch the browser. Bank-grade key isolation.
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#0A0A0A]">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8"><KalkiLogo size={36} /></div>
          <h2 className="font-heading text-2xl font-bold text-[#F5F5F5]">Sign in</h2>
          <p className="text-sm text-[#A3A3A3] mt-1.5">Access your KALKI AI trading terminal.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs text-[#A3A3A3] mb-1.5 block">Email</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@kalkiai.trade" required data-testid="login-email"
                className="bg-[#0A0A0A] border-[#333333] text-[#F5F5F5] focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] mb-1.5 block">Password</Label>
              <Input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password"
                className="bg-[#0A0A0A] border-[#333333] text-[#F5F5F5] focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>

            {err && (
              <div className="text-sm text-[#EF4444] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-sm px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C74B] disabled:opacity-60 text-[#0A0A0A] font-semibold rounded-sm py-2.5 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#262626]">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#525252] mb-3">Demo Accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  onClick={() => { setEmail(d.email); setPassword(d.password); setErr(""); }}
                  data-testid={`demo-fill-${d.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-left rounded-sm border border-[#262626] bg-[#141414] px-3 py-2.5 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="text-xs font-medium text-[#F5F5F5]">{d.label}</div>
                  <div className="text-[10px] text-[#737373] truncate font-mono mt-0.5">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
